import React, { useState } from 'react';
import { DataTableColumn, ExpandableDataTableProps } from '../../models/DataTable';

const ExpandableDataTable = <T,>({
    columns, data, sortBy, sortDirection, onSort, disabled,
    getRowKey, renderRow, renderExpandedContent, onExpand, multiExpand = false
}: ExpandableDataTableProps<T>) => {
    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

    const getSortIcon = (col: DataTableColumn) => {
        if (!col.sortable) return null;
        if (sortBy !== col.key) return <i className="ri-expand-up-down-line" style={{ opacity: 0.3, marginLeft: '4px' }} />;
        return sortDirection === 'asc'
            ? <i className="ri-arrow-up-s-line" style={{ marginLeft: '4px' }} />
            : <i className="ri-arrow-down-s-line" style={{ marginLeft: '4px' }} />;
    };

    const toggle = (key: string, item: T) => {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                if (!multiExpand) next.clear();
                next.add(key);
                onExpand?.(item);
            }
            return next;
        });
    };

    return (
        <table className="table">
            <thead>
                <tr>
                    {columns.map(col => (
                        <th
                            key={col.key}
                            onClick={col.sortable && !disabled ? () => onSort(col.key) : undefined}
                            style={col.sortable ? { cursor: disabled ? 'default' : 'pointer', userSelect: 'none', whiteSpace: 'nowrap', opacity: disabled ? 0.4 : 1 } : undefined}
                        >
                            {col.label}{getSortIcon(col)}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {data.map((item, index) => {
                    const key = getRowKey(item);
                    const isExpanded = expandedKeys.has(key);
                    const onToggle = () => toggle(key, item);
                    return (
                        <React.Fragment key={key}>
                            {renderRow(item, isExpanded, onToggle, index)}
                            {isExpanded && (
                                <tr className="table-details-row">
                                    <td colSpan={columns.length}>
                                        {renderExpandedContent(item)}
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </table>
    );
};

export default ExpandableDataTable;
